// src/utils/folderSetupFirmex.ts
import axios from "axios";
import type { AxiosResponse } from "axios";
import { getToken } from "./tokenHelper";
import { createFolderInSharePoint, shortenPathProportionally } from "./sharepointupload";
import { graphRateLimiter } from "./sharePointTree";

import type { IndexRow } from "../vdr/providers";

console.log("🧱 folderSetupFirmex.ts loaded");

// SharePoint limits: 256 characters for folder/file names
const MAX_NAME_LENGTH = 256;

/**
 * Sanitize folder name by:
 * 1. Replacing SharePoint invalid characters (\ / : * ? " < > | #) with "-"
 * 2. Truncating to 256 characters max (from the right side)
 */
function sanitizeFolderName(name: string): string {
  if (!name) return name;

  // Replace invalid characters
  let sanitized = name
    .replace(/[\/\\:*?"<>|#]/g, '-')
    .trim();

  // Truncate from right if too long
  if (sanitized.length > MAX_NAME_LENGTH) {
    sanitized = sanitized.substring(0, MAX_NAME_LENGTH).trim();
    console.warn(`   ⚠️ [Firmex] Folder name truncated to ${MAX_NAME_LENGTH} chars: "${sanitized}"`);
  }

  return sanitized;
}

/**
 * Convert a Firmex absolute index code + its root folder name into the scoped
 * map key used by the SharePoint sync and the upload preview.
 *
 * The index parser assigns each root an artificial counter ("1", "2", …) and
 * builds absolute codes from it (e.g. R2 VDR = 2nd root → "2.1.6.1.6"). The sync
 * keys folders under a root by the root NAME plus the code relative to that root:
 * "__root:R2 VDR::1.6.1.6". This helper bridges the two languages so creation,
 * routing, and the preview all resolve folders identically.
 *
 * Examples:
 *   ("2",         "R2 VDR") → "__root:R2 VDR"
 *   ("2.1",       "R2 VDR") → "__root:R2 VDR::1"
 *   ("2.1.6.1.6", "R2 VDR") → "__root:R2 VDR::1.6.1.6"
 */
export function toScopedFirmexKey(indexCode: string, rootFolderName: string): string {
  const root = String(rootFolderName || "").trim();
  const code = String(indexCode || "").trim();
  // No dot = the root folder itself → the bare "__root:Name" key.
  if (!code.includes(".")) return `__root:${root}`;
  // Drop the first segment (the root counter); the rest is the code relative to
  // the root, which is exactly what the sync uses as the scoped-key suffix.
  const relCode = code.slice(code.indexOf(".") + 1);
  return `__root:${root}::${relCode}`;
}

// Concurrency cap for folder creation per depth level
const FOLDER_CONCURRENCY = 5;

// ---------- helpers: list children by parent itemId ----------
type Child = { id: string; name: string; folder?: any };
type ChildrenResponse = { value?: Child[]; "@odata.nextLink"?: string };

async function listChildren(
  driveId: string,
  parentItemId: string,
  token: string
): Promise<Child[]> {
  const result: Child[] = [];
  let url: string | null =
    `https://graph.microsoft.com/v1.0/drives/${driveId}/items/${parentItemId}/children?` +
    `$select=id,name,folder&$top=999`;
  console.log(`📜 [Firmex] listChildren: drive=${driveId} parent=${parentItemId}`);
  while (url) {
    await graphRateLimiter.acquire();
    const res: AxiosResponse<ChildrenResponse> = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });
    console.log(`   ↳ status ${res.status}`);
    if (res.status !== 200) {
      console.error(`   ❌ listChildren failed`, res.status, res.data);
      throw new Error(`Failed to list children (${res.status})`);
    }
    const chunk = res.data.value || [];
    console.log(`   ↳ fetched ${chunk.length} children`);
    chunk.forEach((c) => result.push(c));
    url = res.data["@odata.nextLink"] || null;
  }
  console.log(`   ✅ total children: ${result.length}`);
  return result;
}

/**
 * For Firmex: match folder by display number (omit root, keep rest).
 *
 * Examples:
 * - Code "1" → looks for folders starting with "1.", "1-", "1 ", or exactly "1"
 * - Code "1.1" → looks for folders starting with "1.", "1-", "1 " (omit root "1")
 * - Code "1.1.1" → looks for folders starting with "1.1.", "1.1-", "1.1 " (omit root "1")
 * - Code "2.3.4.5" → looks for folders starting with "3.4.5.", "3.4.5-", "3.4.5 " (omit root "2")
 */
function childMatchesFirmexCode(name: string, fullCode: string): boolean {
  const segments = fullCode.split(".");
  const isRoot = segments.length === 1;

  let n = String(name || "").toLowerCase().trim();

  // Firmex sometimes pads root folder numbers with leading zeros (e.g. "01. Name").
  // Normalize them away so "01. foo" matches code "1".
  if (isRoot) n = n.replace(/^0+(\d)/, "$1");

  // Calculate display number (same logic as getFirmexDisplayName)
  const displayNumber = isRoot
    ? segments[0]  // "1" → "1"
    : segments.slice(1).join(".");  // "1.1.1" → "1.1"

  // Match patterns: "1.1. Name", "1.1-Name", "1.1 Name", or exactly "1.1"
  const patterns = [
    `${displayNumber}.`,  // "1.1. Something"
    `${displayNumber}-`,  // "1.1-Something"
    `${displayNumber} `,  // "1.1 Something"
  ];

  // Check exact match first
  if (n === displayNumber.toLowerCase()) return true;

  // Check prefix patterns
  for (const pattern of patterns) {
    if (n.startsWith(pattern.toLowerCase())) {
      return true;
    }
  }

  return false;
}

/**
 * Create the display name for a folder.
 * For Firmex, we use relative numbering (omit root, keep rest).
 * Sanitizes the title to ensure SharePoint compatibility.
 *
 * Examples:
 * - Code "1", Name "Trx" → "1. Trx" (root: show full code)
 * - Code "1.1", Name "Trx 2024" → "1. Trx 2024" (depth 2: omit root "1", show "1")
 * - Code "1.1.1", Name "Trx 2024 Q4" → "1.1. Trx 2024 Q4" (depth 3: omit root "1", show "1.1")
 * - Code "1.1.1.1", Name "January" → "1.1.1. January" (depth 4: omit root "1", show "1.1.1")
 */
function getFirmexDisplayName(fullCode: string, title: string): string {
  const segments = fullCode.split(".");
  const titleTrimmed = title.trim();

  // Relative display number: root (depth 1) shows the full code; deeper levels
  // omit the root segment. e.g. "1" → "1", "1.1.1" → "1.1".
  const displayNumber = segments.length === 1
    ? segments[0]
    : segments.slice(1).join(".");

  // Only skip prepending when the title ALREADY starts with exactly this code
  // (e.g. "1.6. Tax" for displayNumber "1.6"). A title that merely happens to
  // start with some other number — a year like "2021 Tax Returns" or a date like
  // "2026.06.30" — must still get its folder code, or the number is lost in SharePoint.
  const nextChar = titleTrimmed.charAt(displayNumber.length);
  const alreadyPrefixed = titleTrimmed.startsWith(displayNumber) && /[.\-\s]/.test(nextChar);
  if (alreadyPrefixed) {
    return sanitizeFolderName(titleTrimmed);
  }

  return `${displayNumber}. ${sanitizeFolderName(titleTrimmed)}`;
}

// ---------- main: create folders (IDs in, IDs out) ----------
/**
 * Create/ensure a Firmex folder tree INSIDE the picked folder.
 * Uses relative numbering for folder names.
 * 
 * @param driveId      Document library driveId (from picker)
 * @param rootItemId   Picked folder's itemId (from picker)
 * @param rows         Parsed rows (code + name)
 * @returns { map: code -> itemId }
 */
export async function createFirmexFoldersById(
  driveId: string,
  rootItemId: string,
  rows: IndexRow[],
  existingMap?: Record<string, string>,
  preloadedCache?: Map<string, { id: string; name: string; folder?: any }[]>
): Promise<{
  map: Record<string, string>;
  created: number;
  reused: number;
  skipped: number;
  warnings: string[];
}> {
  const token = await getToken();
  const warnings: string[] = [];
  // Seed run-local map with codes already known from the last sync, so children
  // of pre-existing folders can resolve their parents without rediscovering.
  const map: Record<string, string> = { ...(existingMap ?? {}) };
  let created = 0,
    reused = 0,
    skipped = 0;

  console.log(`📂 [Firmex] Start createFirmexFoldersById`);
  console.log(`   driveId=${driveId}`);
  console.log(`   rootItemId=${rootItemId}`);
  console.log(`   input rows=${rows.length}`);
  console.log(`   pre-known codes=${Object.keys(existingMap ?? {}).length}`);

  // Filter out document rows (only process folders)
  const folderRows = rows.filter(r => !r.meta?.isDocument);
  console.log(`   folder rows=${folderRows.length}`);

  // Look up a folder's title by its absolute code, for human-readable logs.
  const titleByCode = new Map(folderRows.map(r => [r.code, r.title]));

  // Sort by depth (shallow to deep)
  const sorted = [...folderRows].sort((a, b) => {
    const da = a.code.split(".").length;
    const db = b.code.split(".").length;
    return (
      da - db ||
      a.code.localeCompare(b.code, undefined, { numeric: true })
    );
  });

  console.log(`   sorted rows=${sorted.length}`);
  if (sorted.length === 0) {
    console.warn("   ⚠️ No rows to process.");
  }

  const childrenCache: Map<string, Child[]> = preloadedCache
    ? new Map(preloadedCache)
    : new Map();

  // Group rows by depth so we can process depth-by-depth, but siblings in parallel.
  const rowsByDepth = new Map<number, IndexRow[]>();
  for (const r of sorted) {
    const depth = r.code.split(".").length;
    if (!rowsByDepth.has(depth)) rowsByDepth.set(depth, []);
    rowsByDepth.get(depth)!.push(r);
  }

  const depths = Array.from(rowsByDepth.keys()).sort((a, b) => a - b);

  // Helper: process a single row
  const processRow = async (r: IndexRow) => {
    const code = r.code.trim();
    const rootName = (r.meta?.rootFolderName as string) || "";
    // The scoped key is the shared language between the index, the sync map,
    // and the preview. All map reads/writes below use it — never the raw code.
    const myKey = toScopedFirmexKey(code, rootName);

    // Skip rows already resolved from a prior sync — no Graph calls needed.
    if (existingMap?.[myKey]) {
      map[myKey] = existingMap[myKey];
      reused++;
      console.log(`   ⏭️ [Firmex] Skipping ${myKey} — already in existing map`);
      return;
    }

    const parentCode = code.includes(".")
      ? code.slice(0, code.lastIndexOf("."))
      : "";
    const parentKey = parentCode ? toScopedFirmexKey(parentCode, rootName) : null;
    const parentItemId = parentKey ? map[parentKey] : rootItemId;

    console.log(
      `➡️ [Firmex] Processing ${myKey} name="${r.title}" parent=${
        parentKey || "(root)"
      } parentItemId=${parentItemId || "?"}`
    );

    if (!parentItemId) {
      skipped++;
      const msg = `Parent not found for ${myKey} ("${r.title}"). Skipped.`;
      warnings.push(msg);
      console.warn(`   ⚠️ ${msg}`);
      return;
    }

    // Try to find an existing folder first (reuse).
    if (!childrenCache.has(parentItemId)) {
      console.log(`   🗂️ loading children for parent=${parentItemId}`);
      childrenCache.set(
        parentItemId,
        await listChildren(driveId, parentItemId, token)
      );
    }
    const list = childrenCache.get(parentItemId)!;
    // Parent is already resolved to the correct SP folder, so number matching
    // is naturally bounded to that parent's own children — a same-numbered
    // folder elsewhere in the tree can't be reached from here.
    const hit = list.find((c) => c.folder && childMatchesFirmexCode(c.name, code));

    // Human-readable parent identity for logs: its number (relative code) + title.
    const parentNum = parentKey
      ? (parentKey.includes("::") ? parentKey.split("::")[1] : "(root)")
      : "(picked root folder)";
    const parentTitle = parentCode
      ? (titleByCode.get(parentCode) ?? "(not in index)")
      : "(picked root folder)";

    if (hit) {
      map[myKey] = hit.id;
      reused++;
      console.log(
        `   ♻️ Reused "${hit.name}" (${hit.id}) for ${myKey} → parent number "${parentNum}" name "${parentTitle}"`
      );
      return;
    }

    // Otherwise, create with relative numbering
    let display = getFirmexDisplayName(code, r.title || "");
    console.log(
      `   ➕ Creating "${display}" → parent number "${parentNum}" name "${parentTitle}" (parentItemId=${parentItemId})`
    );

    let newId: string;
    try {
      newId = await createFolderInSharePoint(driveId, parentItemId, display);
    } catch (err: any) {
      const msg = (err?.message ?? "").toLowerCase();
      const isPathError = msg.includes("400") &&
        (msg.includes("name") || msg.includes("path") || msg.includes("length") || msg.includes("invalid"));

      if (!isPathError) throw err;

      // Path too long — proportionally shorten ancestors and retry. The
      // Firmex display name is "<relativeNum>. <title>", so the floor we
      // never cut below is the leading numeric prefix.
      console.log(`   📏 [Firmex] Path too long for "${display}". Shortening ancestors proportionally…`);
      const budget = await shortenPathProportionally(driveId, parentItemId, display.length, rootItemId);

      if (budget > 0 && budget < display.length) {
        const prefixMatch = display.match(/^\d+(?:\.\d+)*\./);
        const prefix = prefixMatch ? prefixMatch[0] : "";
        display = budget <= prefix.length
          ? prefix
          : display.substring(0, Math.max(budget, prefix.length + 1)).trim();
        console.log(`   ✂️ [Firmex] Folder name adjusted to fit budget (${budget}): "${display}"`);
      }

      newId = await createFolderInSharePoint(driveId, parentItemId, display);
      console.log(`   ✅ [Firmex] created child id=${newId} (after path shortening)`);
    }

    // Invalidate cache so siblings see the new item
    childrenCache.delete(parentItemId);
    map[myKey] = newId;
    created++;
    console.log(`   ✅ created child id=${newId} for ${myKey}`);
  };

  // Process depth-by-depth. Within a depth, use a simple concurrency pool.
  for (const depth of depths) {
    const batch = rowsByDepth.get(depth)!;
    console.log(
      `📏 [Firmex] Processing depth=${depth} with ${batch.length} row(s) (parallel up to ${FOLDER_CONCURRENCY})`
    );

    const queue = [...batch];

    const worker = async () => {
      while (true) {
        const r = queue.shift();
        if (!r) break;
        await processRow(r);
      }
    };

    const workerCount =
      Math.min(FOLDER_CONCURRENCY, queue.length || 0) || 1;
    const workers = Array.from({ length: workerCount }, () => worker());
    await Promise.all(workers);
  }

  console.log(
    `✅ [Firmex] Done createFirmexFoldersById: created=${created}, reused=${reused}, skipped=${skipped}`
  );
  if (warnings.length) {
    console.warn(`   ⚠️ warnings (${warnings.length}):`, warnings);
  }
  return { map, created, reused, skipped, warnings };
}

/**
 * For a file number like "1.3.2.1", return the target folder code "1.3.2".
 * For a single number like "23", return null (means upload to the picked root).
 */
export function parentCodeForFirmexFileNumber(fileNumber: string): string | null {
  const parts = String(fileNumber || "").split(".").filter(Boolean);
  if (parts.length <= 1) return null;
  return parts.slice(0, -1).join(".");
}

/**
 * Build a mapping from (rootFolderName, relativeCode) to absolute code.
 *
 * Example:
 * - Folder with code "1.22", rootFolderName "1 Transaction Documents"
 * - Display name is "22. Restricted Cash" (omit root "1", show "22")
 * - Maps ("1 Transaction Documents", "22") → "1.22"
 *
 * This allows files to find the correct parent folder even when multiple roots have the same relative numbering.
 */
export function buildRelativeToAbsoluteMap(rows: IndexRow[]): Map<string, string> {
  const map = new Map<string, string>();

  // Only process folder rows
  const folders = rows.filter(r => !r.meta?.isDocument);

  for (const row of folders) {
    const { code, meta } = row;
    const rootFolderName = (meta?.rootFolderName as string) || "";
    const segments = code.split(".");

    // Calculate the relative code (same logic as display number)
    const relativeCode = segments.length === 1
      ? segments[0]  // "1" → "1"
      : segments.slice(1).join(".");  // "1.22" → "22", "1.1.1" → "1.1"

    // Create a composite key: rootFolderName|relativeCode
    const key = `${rootFolderName}|${relativeCode}`;
    map.set(key, code);
  }

  return map;
}

/**
 * Firmex-specific parent code resolver for file uploads.
 *
 * Given a file code like "22.1" and the index rows:
 * 1. Find the document row with this code to get its root folder
 * 2. Extract parent code "22" (relative)
 * 3. Look up (rootFolder, "22") in the mapping to get absolute code
 * 4. Return the absolute code (e.g., "1.22")
 */
export function resolveFirmexFileParent(
  fileCode: string,
  rows: IndexRow[]
): string | null {
  const parts = fileCode.split(".").filter(Boolean);
  if (parts.length <= 1) return null; // No parent (top-level file)

  // Find the document row with this code to determine which root folder it belongs to
  const docRow = rows.find(r => r.code === fileCode && r.meta?.isDocument);
  if (!docRow) {
    console.warn(`   ⚠️ Firmex: No document found for code "${fileCode}"`);
    return null;
  }

  const rootFolderName = (docRow.meta?.rootFolderName as string) || "";
  console.log(`   🔍 Firmex: Document "${fileCode}" belongs to root "${rootFolderName}"`);

  // Get parent code (e.g., "22.1" → "22")
  const relativeParent = parts.slice(0, -1).join(".");

  // Build mapping
  const mapping = buildRelativeToAbsoluteMap(rows);

  // Look up using composite key: rootFolderName|relativeCode
  const key = `${rootFolderName}|${relativeParent}`;
  const absoluteCode = mapping.get(key);

  if (absoluteCode) {
    console.log(`   🔍 Firmex file routing: "${fileCode}" → root="${rootFolderName}" parent="${relativeParent}" → absolute "${absoluteCode}"`);
    return absoluteCode;
  }

  console.warn(`   ⚠️ Firmex: No absolute code found for root="${rootFolderName}" relative="${relativeParent}"`);
  return null;
}

/**
 * Clean filename by removing leading number and file extension.
 * Examples:
 * - "2 June 2025.pdf" → "June 2025"
 * - "11 Marketing Materials.xlsx" → "Marketing Materials"
 * - "132 Report.docx" → "Report"
 */
function cleanFilename(fileName: string): string {
  // Remove file extension
  const nameWithoutExt = fileName.replace(/\.[^.]+$/, "");

  // Remove leading number (one or more digits followed by space)
  const cleaned = nameWithoutExt.replace(/^\d+\s+/, "").trim();

  return cleaned;
}

/**
 * Canonicalize a name for matching a downloaded filename against a Column D
 * title. VDR downloads deterministically rewrite names, so we collapse ALL of
 * those variations to the same shape on both sides before comparing:
 *   - strip accents/diacritics ("Café" → "cafe")
 *   - lowercase
 *   - reduce every run of non-alphanumeric characters (spaces, underscores,
 *     dashes, plus signs, parentheses, punctuation, and the "-" that VDRs
 *     substitute for invalid chars like \ / : * ? " < > |) to a single space
 */
function normalizeFirmexName(name: string): string {
  return String(name || "")
    .normalize("NFD").replace(/\p{Diacritic}/gu, "") // strip accents: é → e
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")                       // any separator/punctuation → space
    .trim();
}

/**
 * Match folder name from Column O path to folder title, accounting for numbering.
 * Examples:
 * - "Financial Updates" matches folder with title "Financial Updates"
 * - "June 2025" matches folder with title "June 2025"
 * - "2. Financials" matches folder with title "2. Financials"
 */
function folderMatchesPathSegment(folderTitle: string, pathSegment: string): boolean {
  // Direct match
  if (folderTitle === pathSegment) return true;

  // Strip leading numbers from both and compare
  const cleanFolder = folderTitle.replace(/^\d+\.\s*/, "").trim();
  const cleanSegment = pathSegment.replace(/^\d+\.\s*/, "").trim();

  return cleanFolder === cleanSegment;
}

/**
 * Path-based routing for Firmex files without hierarchical numbering.
 *
 * Example:
 * - File: "2 June 2025.pdf" → clean to "June 2025"
 * - Find document row with title "June 2025"
 * - Get Column O: "\2. Financials\Financial Updates\June 2025"
 * - Navigate path to find folder code
 * - Return the folder code
 */
export function resolveFirmexFileByPath(
  fileName: string,
  rows: IndexRow[]
): string | null {
  // Extract leading number and clean the filename
  const leadingNumberMatch = fileName.match(/^(\d+)\s+/);
  const leadingNumber = leadingNumberMatch ? leadingNumberMatch[1] : null;
  const cleanedName = cleanFilename(fileName);

  console.log(`   🔍 Firmex path routing: "${fileName}" → code="${leadingNumber}" cleaned="${cleanedName}"`);

  // Find document row by matching BOTH code and title. Canonicalize both sides
  // so VDR download rewrites (spaces↔underscores, invalid chars → dashes,
  // accents, punctuation) don't block the match.
  const normalizedCleanedName = normalizeFirmexName(cleanedName);

  console.log(`   🔍 Normalized cleaned name: "${normalizedCleanedName}"`);

  const docRow = rows.find(r => {
    if (!r.meta?.isDocument) return false;

    // Match title with the same canonicalization
    const normalizedTitle = normalizeFirmexName(r.title || "");

    if (normalizedTitle !== normalizedCleanedName) return false;

    // If file has a leading number, also match the code
    if (leadingNumber) {
      const docCode = r.code || "";
      // Match if code equals leading number OR ends with ".{leadingNumber}"
      if (docCode === leadingNumber) return true;
      if (docCode.includes(".") && docCode.split(".").pop() === leadingNumber) return true;
      return false;
    }

    // No leading number: match by title only
    return true;
  });

  if (!docRow) {
    console.warn(`   ⚠️ Firmex: No document found for code="${leadingNumber}" name="${cleanedName}"`);
    console.warn(`   Normalized: "${normalizedCleanedName}"`);
    const sampleDocs = rows.filter(r => r.meta?.isDocument).slice(0, 5);
    console.warn(`   Sample doc titles (raw):`, sampleDocs.map(r => r.title));
    console.warn(`   Sample doc titles (normalized):`, sampleDocs.map(r => normalizeFirmexName(r.title || "")));
    return null;
  }

  // Get the Complete Folder Path from meta (stored from Column O)
  const completePath = (docRow.meta?.completePath as string) || "";
  if (!completePath) {
    console.warn(`   ⚠️ Firmex: No path found for document "${cleanedName}"`);
    return null;
  }

  console.log(`   🔍 Firmex: Found document "${cleanedName}" with path "${completePath}"`);

  // Parse the path into segments
  const cleaned = completePath.startsWith("\\") ? completePath.slice(1) : completePath;
  const pathSegments = cleaned.split("\\").filter(Boolean);

  if (pathSegments.length === 0) {
    console.warn(`   ⚠️ Firmex: Empty path for document "${cleanedName}"`);
    return null;
  }

  console.log(`   🔍 Firmex: Path segments:`, pathSegments);

  // Get all folders (not documents)
  const folders = rows.filter(r => !r.meta?.isDocument);

  // Navigate the folder hierarchy
  let currentCode = "";

  for (let i = 0; i < pathSegments.length; i++) {
    const segment = pathSegments[i];
    const isRoot = i === 0;

    // Find folder at this level that matches the segment
    const matchingFolder = folders.find(f => {
      // Check if it's at the right depth
      const folderDepth = f.code.split(".").length;
      const expectedDepth = i + 1;
      if (folderDepth !== expectedDepth) return false;

      // Check if it's a child of the current path
      if (!isRoot && !f.code.startsWith(currentCode + ".")) return false;

      // Check if the title matches the segment
      return folderMatchesPathSegment(f.title, segment);
    });

    if (!matchingFolder) {
      console.warn(`   ⚠️ Firmex: No folder found for segment "${segment}" at depth ${i + 1}`);
      return null;
    }

    currentCode = matchingFolder.code;
    console.log(`   🔍 Firmex: Segment "${segment}" → code "${currentCode}"`);
  }

  // The last folder in the path is where the file should go. Convert the
  // absolute index code to the scoped map key so it matches the synced map
  // (the same language creation and the preview use).
  const rootName = (docRow.meta?.rootFolderName as string) || "";
  const scopedKey = toScopedFirmexKey(currentCode, rootName);
  console.log(`   ✅ Firmex: Final folder "${currentCode}" → scoped key "${scopedKey}"`);
  return scopedKey;
}

/**
 * Resolve a Firmex file's target folder using its ZIP-internal directory path.
 *
 * Handles two ZIP shapes:
 *   - Whole-project ZIPs starting at a true top-level root, with deeper
 *     segments numbered relative to that root: "6. Taxes / 12. Payroll / 12.1. Monthly".
 *   - Subtree ZIPs starting mid-tree, with deeper segments holding fully
 *     absolute codes: "2.2. Harquahala / 2.2.9. Construction / 2.2.9.4. Reporting".
 *
 * Algorithm — strict, no fallbacks:
 *   1. rootNum = leading number of segments[0]. Anchors the subtree.
 *   2. For the deepest segment, take its leading number as segNum.
 *      - If segNum already starts with `rootNum.` it's absolute → use as-is.
 *      - Otherwise it's relative-to-root → prepend rootNum.
 *   3. Look up the resulting code in indexMap. If present, return it.
 *      If not, return null and log diagnostics. No walk-up, no root fallback —
 *      a missing code means the index doesn't describe this file's folder
 *      and the caller should surface that, not silently misroute.
 */
export function resolveFirmexFolderByZipPath(
  zipDir: string,
  indexMap: Record<string, string>,
  indexRows?: IndexRow[]
): string | null {
  const segments = zipDir.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const extractNum = (s: string): string | null => {
    const m = s.match(/^\s*(\d+(?:\.\d+)*)/);
    return m ? m[1] : null;
  };

  // If the first segment has no leading number, it may be an unnumbered root folder
  // (e.g. "R2 VDR"). Match by name against root-level index rows and skip if found.
  // When the indexMap was built from a parent of the named root, keys are scoped
  // as "__root:R2 VDR::14.32". Track the root name to build the right key.
  let offset = 0;
  let scopedRootName: string | null = null;
  if (!extractNum(segments[0]) && indexRows?.length) {
    const segName = segments[0].toLowerCase();
    const isKnownRoot = indexRows.some(
      r => !r.meta?.isDocument && !r.code.includes(".") && r.title.toLowerCase() === segName
    );
    if (isKnownRoot) {
      offset = 1;
      scopedRootName = segments[0];
      if (segments.length <= offset) return null;
    } else {
      console.error(`   ❌ Firmex ZIP: "${segments[0]}" has no number and is not a known root folder`);
      return null;
    }
  }

  const rootNum = extractNum(segments[offset])?.replace(/^0+(\d)/, "$1") ?? null;
  if (!rootNum) return null;

  // File sits directly in the first numbered segment — no deeper folder to resolve.
  if (segments.length - offset === 1) {
    const key = scopedRootName ? `__root:${scopedRootName}::${rootNum}` : rootNum;
    if (indexMap[key]) {
      console.log(`   📦 Firmex ZIP: "${zipDir}" → code "${key}"`);
      return key;
    }
    console.error(
      `   ❌ Firmex ZIP: root code "${key}" from "${zipDir}" not in indexMap. ` +
      `Sample indexMap keys: ${Object.keys(indexMap).slice(0, 8).join(", ")}`
    );
    return null;
  }

  const deepestSeg = segments[segments.length - 1];
  const segNum = extractNum(deepestSeg);
  if (!segNum) {
    console.error(
      `   ❌ Firmex ZIP: deepest segment "${deepestSeg}" has no leading number — cannot route "${zipDir}"`
    );
    return null;
  }

  // Subtree ZIPs label deeper folders with absolute codes ("2.2.9.4");
  // whole-project ZIPs label them relative to the root ("9.4"). Detect
  // by prefix and skip the prepend in the absolute case.
  const code = segNum.startsWith(rootNum + ".") ? segNum : `${rootNum}.${segNum}`;
  const lookupKey = scopedRootName ? `__root:${scopedRootName}::${code}` : code;

  if (indexMap[lookupKey]) {
    console.log(`   📦 Firmex ZIP: "${zipDir}" → code "${lookupKey}"`);
    return lookupKey;
  }

  console.error(
    `   ❌ Firmex ZIP: code "${lookupKey}" not in indexMap for "${zipDir}" ` +
    `(rootNum="${rootNum}", deepest segNum="${segNum}"). ` +
    `Sample indexMap keys: ${Object.keys(indexMap).slice(0, 8).join(", ")}`
  );
  return null;
}